import { useCallback, useRef, useEffect } from 'react';

export function useResizablePanel(initialWidth: number = 420, minWidth: number = 320, maxWidth: number = 800) {
  const panelWidth = useRef(initialWidth);
  const isResizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(initialWidth);
  const panelElRef = useRef<HTMLDivElement | null>(null);

  const registerPanel = useCallback((el: HTMLDivElement | null) => {
    panelElRef.current = el;
    if (el) el.style.width = `${panelWidth.current}px`;
  }, []);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    isResizing.current = true;
    startX.current = e.clientX;
    startWidth.current = panelWidth.current;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    e.preventDefault();
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing.current) return;
      const delta = startX.current - e.clientX;
      const newWidth = Math.max(minWidth, Math.min(maxWidth, startWidth.current + delta));
      panelWidth.current = newWidth;
      // 直接操作 DOM，不触发 React 重渲染
      if (panelElRef.current) {
        panelElRef.current.style.width = `${newWidth}px`;
      }
    };

    const handleMouseUp = () => {
      if (isResizing.current) {
        isResizing.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [minWidth, maxWidth]);

  return { panelWidth, handleResizeStart, registerPanel };
}
